import { computed, ref, unref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import constants from '@/constants/constants';
import { f } from '@/utils/vue-formatter';

/**
 * @param {import("vue").ComputedRef} articleRef The article as a computed property.
 */
export function useArticleGridComposable(articleRef)
{
    const store = useStore();
    const { t } = useI18n({ useScope: 'global' });

    // ------------------------------------------------------------ CONSTANTS

    /**
     * Any tag matching PC{number} or PCS{number},
     * i.e. "PC1", "PCS6", "PCS20", etc.
     */
    const piecesTagRegex = /^PCS?(\d+)$/;

    const intendedUseTags = {
        [constants.tags.COMPATIBLE_WITH_SOFT]:           'bg-red-500 text-white',
        [constants.tags.COMPATIBLE_WITH_RGP]:            'bg-red-700 text-white',
        [constants.tags.MULTI_PURPOSE]:                  'bg-green-500 text-white',
        [constants.tags.OXIDIZER]:                       'bg-green-600 text-white',
        [constants.tags.PRESERVATION]:                   'bg-green-700 text-white',
        [constants.tags.CLEANER]:                        'bg-green-800 text-white',
        [constants.tags.MULTI_PACK]:                     'bg-blue-500 text-white',
        [constants.tags.FLIGHT_PACK]:                    'bg-blue-600 text-white',
        [constants.tags.ARTIFICIAL_TEARS]:               'bg-yellow-500 text-white',
        [constants.tags.COMPATIBLE_WITH_HYDROCOLOR_PRO]: 'bg-yellow-600 text-white',
    };

    /**
     * The labels for the tags in keys will be replaced with those of the tags
     * in values.
     * @type {Record<string, string>}
     */
    const intendedUseTagsReplaceLabels = {
        [constants.tags.COMPATIBLE_WITH_SOFT]: constants.tags.SOFT,
        [constants.tags.COMPATIBLE_WITH_RGP]:  constants.tags.RGP,
    };

    // ------------------------------------------------------------ DATA

    const addingToBasket = ref(false);

    // ------------------------------------------------------------ COMPUTED

    /**
     * Disable adding to basket if there's an availability communication.
     */
    const basketDisabled = computed(() =>
    {
        // Never disable basket for SL staff
        if(store.getters['account/isStaff'])
        {
            return false;
        }

        return !!articleRef.value?.extra.availability_communication;
    });

    // ------------------------------------------------------------ METHODS

    /**
     * Get the label for a given "Intended Use" tag.
     * If the tag has a matching entry in `intededUseTagsReplaceLabels`, the
     * label for that tag will be used instead.
     *
     * @param {string} tagCode The tag.
     * @returns {string} The label.
     */
    function getIntendedUseTagLabel(tagCode)
    {
        return store.getters['catalog/getTagLabelByCode'](
            intendedUseTagsReplaceLabels[tagCode] ?? tagCode
        ) ?? tagCode;
    }

    function getFilterBadgesCallback()
    {
        const tags = Object.keys(intendedUseTags);
        return article => article.tags.map(t =>
        {
            if(!tags.includes(t.code))
            {
                return false;
            }

            return {
                tag:   getIntendedUseTagLabel(t.code),
                class: intendedUseTags[t.code],
            };
        }).filter(t => t); // remove empty values
    }

    function getBadgesCallback()
    {
        return article =>
        {
            if(!article.tags)
            {
                return [];
            }

            return article.tags.map(t =>
            {
                if(piecesTagRegex.test(t.code))
                {
                    // Replace PC1 => 1×, PCS6 => 6×, PCS20 => 20×, etc.
                    return t.code.replace(piecesTagRegex, '$1×');
                }

                return false;
            }).filter(t => t); // remove empty values
        };
    }

    function getFormattedUnitPrice(article)
    {
        const units = getUnitsFromTags(article);
        const unitPrice = parseFloat(article.base_price / units).toFixed(2);
        const formattedPrice = article.currency_code + ' ' + f(unitPrice, 'thousand');

        return t('cleaning_products.price_per_unit', {
            price: formattedPrice,
        });
    }

    function getFormattedTotalPrice(article)
    {
        const price = parseFloat(article.base_price).toFixed(2);
        return article.currency_code + ' ' + f(price, 'thousand');
    }

    /**
     * Retrieve how many units are sold as one article.
     */
    function getUnitsFromTags(article)
    {
        const tag = article.tags.find(t => piecesTagRegex.test(t.code));
        return parseInt(tag?.code.replace(piecesTagRegex, '$1')) || 1;
    }

    async function addToBasket(article, quantity = 1)
    {
        if(basketDisabled.value)
        {
            return;
        }

        // Only add one piece at a time
        if(addingToBasket.value)
        {
            return;
        }

        addingToBasket.value = true;

        const basketLine = {
            article_code: article.code,
            data:         null,
            quantity,
        };

        try
        {
            await store.dispatch('basket/saveLines', basketLine);
        }
        catch(error)
        {
            // TODO: Display error
        }
        finally
        {
            addingToBasket.value = false;
        }
    }

    /**
     * Replace new lines with `<br>` tags in a string.
     *
     * @param {string} str
     * @returns {string}
     */
    function nl2br(str)
    {
        return `${str}`.replace(/\r\n|\r|\n/g, '<br>');
    }

    // ------------------------------------------------------------ EXPOSE

    return {
        // Constants
        piecesTagRegex,
        intendedUseTags,

        // Data
        addingToBasket,
        basketDisabled,

        // Computed

        // Methods
        getIntendedUseTagLabel,
        getFilterBadgesCallback,
        getBadgesCallback,
        getFormattedUnitPrice,
        getFormattedTotalPrice,
        getUnitsFromTags,
        addToBasket,
        nl2br,
    };
};
